let scriptPromise
let registration

function errorHint (code) {
  return '验证失败，请重试' + (/^\d{6}$/.test(String(code)) ? `（错误码 ${code}）` : '')
}

// One inline surface owns a token bound to its page's verification action.
export function mountRegistration (container, sitekey, onState, action = 'signup') {
  let api, widgetId, token = '', expires = 0, timer, generation = 0, disposed = false
  const pause = () => {
    generation++
    clearTimeout(timer)
    token = ''; expires = 0
    onState(false, '')
    if (api && widgetId !== undefined) api.remove(widgetId)
    widgetId = undefined
    container.replaceChildren()
  }
  const start = async () => {
    if (disposed) return
    pause()
    const current = generation
    try {
      api = await loadScript()
      if (disposed || current !== generation) return
      const invalidate = message => {
        if (disposed || current !== generation) return
        token = ''; expires = 0; clearTimeout(timer)
        onState(false, message)
      }
      widgetId = api.render(container, {
        sitekey, action, size: 'normal', theme: 'light',
        callback: value => {
          if (disposed || current !== generation) return
          token = value; expires = Date.now() + 280000
          clearTimeout(timer)
          timer = setTimeout(() => invalidate('验证已过期，请重新验证'), 280000)
          onState(Boolean(value), '')
        },
        'expired-callback': () => invalidate('验证已过期，请重新验证'),
        'timeout-callback': () => invalidate('验证超时，请重新验证'),
        'error-callback': code => { invalidate(errorHint(code)); return true }
      })
    } catch (error) {
      if (!disposed && current === generation) onState(false, '验证加载失败，请重试')
    }
  }
  const owner = {
    start, pause, action,
    take (key) {
      if (disposed || key !== sitekey || !token || Date.now() >= expires) {
        pause(); start()
        throw new Error('请先完成人机验证')
      }
      const value = token
      pause()
      return value
    },
    dispose () { disposed = true; pause(); if (registration === owner) registration = null }
  }
  if (registration) registration.dispose()
  registration = owner
  start()
  return owner
}

export function resetRegistration (action = 'signup') {
  if (registration && registration.action === action) registration.start()
}

// Capture before asynchronous work; a late request cannot reset a new page.
export function captureVerificationReset (action) {
  const owner = registration
  return () => {
    if (owner && registration === owner && owner.action === action) owner.start()
  }
}

function loadScript () {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    let timer
    const fail = () => {
      clearTimeout(timer)
      script.remove()
      scriptPromise = null
      reject(new Error('人机验证加载失败，请稍后重试'))
    }
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
    script.async = true
    script.onerror = fail
    script.onload = () => {
      clearTimeout(timer)
      if (!window.turnstile) return fail()
      resolve(window.turnstile)
    }
    timer = setTimeout(fail, 15000)
    document.head.appendChild(script)
  })
  return scriptPromise
}

// Registration shares one inline area; other existing surfaces retain their
// modal. Each attempt owns a fresh widget and never reuses a redeemed token.
export function challenge (sitekey, action) {
  if (action === 'signup' || action === 'login') {
    try {
      if (!registration || registration.action !== action) throw new Error('请先完成人机验证')
      return Promise.resolve(registration.take(sitekey))
    } catch (error) { return Promise.reject(error) }
  }
  const owner = action === 'signup_email' ? registration : null
  if (owner) owner.pause()
  return new Promise((resolve, reject) => {
    const inline = (action === 'signup' || action === 'signup_email') && document.querySelector('[data-register-human-verification]')
    const dialog = inline || document.createElement('dialog')
    if (inline) inline.replaceChildren()
    dialog.setAttribute('aria-label', '人机验证')
    if (!inline) dialog.style.cssText = 'border:0;border-radius:8px;padding:20px;max-width:90vw;background:white;color:#303133'
    const title = document.createElement('p')
    title.textContent = action === 'signup_email' ? '完成安全验证后自动发送验证码' : '完成安全验证后自动继续'
    title.setAttribute('role', 'status')
    const container = document.createElement('div')
    const cancel = document.createElement('button')
    cancel.type = 'button'
    cancel.textContent = '取消'
    cancel.style.cssText = 'display:block;margin-top:16px;padding:8px 16px'
    dialog.append(title, container, cancel)
    let widgetId
    let api
    let settled = false
    const oldFocus = document.activeElement
    const finish = (error, token) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      window.removeEventListener('hashchange', onNavigate)
      if (api && widgetId !== undefined) api.remove(widgetId)
      if (inline) {
        inline.replaceChildren(title)
        title.textContent = error ? error.message + '，请重新点击发送验证码或注册' : '本次安全验证已通过；再次操作将重新验证'
      } else {
        dialog.remove()
        if (oldFocus && oldFocus.isConnected) oldFocus.focus()
      }
      if (error) reject(error)
      else resolve(token)
    }
    const onNavigate = () => finish(new Error('页面已切换，请重新验证'))
    const timer = setTimeout(() => finish(new Error('人机验证超时，请重试')), 120000)
    cancel.onclick = () => finish(new Error('已取消人机验证'))
    if (!inline) dialog.addEventListener('cancel', event => { event.preventDefault(); cancel.onclick() })
    window.addEventListener('hashchange', onNavigate)
    if (inline) {
      inline.scrollIntoView({ block: 'center', behavior: 'smooth' })
    } else {
      document.body.appendChild(dialog)
      try { dialog.showModal() } catch (error) { finish(new Error('浏览器不支持安全验证，请升级浏览器')); return }
    }
    loadScript().then(turnstile => {
      if (settled) return
      api = turnstile
      widgetId = api.render(container, {
        sitekey, action, size: 'normal', theme: 'light',
        callback: token => finish(null, token),
        'error-callback': code => { finish(new Error(errorHint(code))); return true },
        'expired-callback': () => finish(new Error('人机验证已过期，请重试')),
        'timeout-callback': () => finish(new Error('人机验证超时，请重试'))
      })
    }).catch(error => finish(error))
  }).finally(() => { if (owner && registration === owner) owner.start() })
}
